<template>
    <div>
        <div v-if="!passcodeSent">
            <div>
                <h1 class="mb-1.5 mt-14">Send one-time code</h1>
                <p class="mb-4 text-sm text-ds-primary-500">
                    Your account requires multi-factor authentication. Select a delivery method for your code to complete log in.
                </p>
            </div>
            <DsButton
                v-if="includeBackButton"
                class="mb-4 text-sm"
                size="sm"
                variant="link-button"
                @click="handleBackButton"
            >
                ← Change phone number
            </DsButton>
            <div
                v-if="clientPhoneIsTextable"
                class="mb-2.5 rounded border border-ds-primary-300 p-4"
                @click="selectPhoneType('sms')"
            >
                <DsRadio
                    v-model="phoneType"
                    :label="`Text ${phoneNumber}`"
                    value="sms"
                />
            </div>
            <div
                class="mb-2.5 rounded border border-ds-primary-300 p-4"
                @click="selectPhoneType('call')"
            >
                <DsRadio
                    v-model="phoneType"
                    :label="`Call ${phoneNumber}`"
                    value="call"
                />
            </div>
            <a
                class="text-sm text-ds-primary-500"
                href="javascript:void(0)"
                @click="alreadyHasPasscode"
            >
                Already have a code?
            </a>
            <DsAlertBanner
                v-if="sendPasscodeError"
                class="mt-6"
                role="alert"
                :title="sendPasscodeError"
                variant="error"
            />
            <DsButton
                v-if="phoneType !== null"
                class="my-10 block w-full rounded bg-ds-primary-900 px-4 py-2"
                data-testid="btn-send-code"
                size="lg"
                type="button"
                variant="primary"
                @click="sendPasscode"
            >
                {{ sendCodeButton }}
            </DsButton>
        </div>
        <div v-if="passcodeSent">
            <div class="my-3">
                <div>
                    <h1 class="mb-1.5 mt-14">Enter one-time code</h1>
                    <p class="mb-4 text-sm text-ds-primary-500">
                        Your account requires multi-factor authentication. Enter the one-time use code you received through the
                        delivery method specified to complete log in.
                    </p>
                </div>
                <CodeInput
                    :error="!!validatePasscodeError"
                    type="text"
                    @code="captureCode"
                    @keyup.enter="validatePasscode"
                />
                <DsAlertBanner
                    v-if="validatePasscodeError"
                    class="my-6"
                    role="alert"
                    :title="validatePasscodeError"
                    variant="error"
                />
                <a
                    class="text-sm text-ds-primary-500"
                    href="javascript:void(0)"
                    @click="resendCode"
                >
                    Resend Code
                </a>
            </div>
            <div class="mt-24">
                <button
                    class="text-uppercase my-10 block w-full rounded bg-ds-primary-900 px-4 py-2 text-center font-medium text-white hover:bg-semantic-primary-hover focus:outline-none focus:ring-2 focus:ring-semantic-primary-hover focus:ring-offset-2"
                    data-testid="btn-login"
                    type="button"
                    @click="validatePasscode"
                >
                    Log In
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useClientApi } from '@/api/client/api-client';
import type { PhoneType, TwilioPhoneType } from '@/api/interfaces/twilio';
import DsAlertBanner from '@ds/alert-banner/DsAlertBanner.vue';
import DsButton from '@ds/button/DsButton.vue';
import DsRadio from '@ds/form/input/DsRadio.vue';
import type { OnFetchErrorContext } from '@vueuse/core';
import { computed, onMounted, ref } from 'vue';
import CodeInput from './CodeInput.vue';

const props = withDefaults(
    defineProps<{
        clientPhoneType: TwilioPhoneType;
        phoneNumber: string;
        includeBackButton?: boolean;
    }>(),
    {
        includeBackButton: false,
    },
);

const emit = defineEmits<{
    passcodeValid: [phoneType: PhoneType];
    passcodeSent: [phoneType: PhoneType];
    backButtonPressed: [];
}>();

const passcode = ref('');
const phoneType = ref<PhoneType>('sms');
const passcodeSent = ref(false);
const validatePasscodeError = ref('');
const sendPasscodeError = ref('');

const sendCodeButton = computed(() => {
    return phoneType.value === 'call' ? 'Call Me' : 'Send Code';
});

const clientPhoneIsTextable = computed(() => {
    return props.clientPhoneType === 'mobile' || props.clientPhoneType === 'voip';
});

const clientApi = useClientApi();

async function sendPasscode() {
    const { error } = await clientApi('/client/login/sendPasscode', {
        onFetchError(ctx: OnFetchErrorContext) {
            /**
             * Here, `onFetchError` is intercepting the response data and setting `error` to `data`
             * because the error messages are found in `context.data`, not `context.error`.
             * */
            const { data } = ctx;
            const errorMessage = typeof data === 'string' ? data : data?.error_message;
            ctx.error = errorMessage;

            return ctx;
        },
    })
        .post({
            phoneType: phoneType.value,
        })
        .json();

    if (error.value) {
        sendPasscodeError.value =
            error.value ||
            'The phone number provided can not be reached. Please contact your title company to correct your contact info.';
    } else {
        passcodeSent.value = true;
        emit('passcodeSent', phoneType.value);
        sendPasscodeError.value = '';
    }
}

async function validatePasscode() {
    const { data, error } = await clientApi('/client/login/validatePasscode', {
        onFetchError(ctx: OnFetchErrorContext) {
            /**
             * Here, `onFetchError` is intercepting the response data and setting `error` to `data`
             * because the error messages are found in `context.data`, not `context.error`.
             * */
            const { data } = ctx;
            const passcodeErrors = data?.errors?.passcode;
            ctx.error = passcodeErrors;

            return ctx;
        },
    })
        .post({
            passcode: passcode.value,
        })
        .json();

    if (error.value) {
        validatePasscodeError.value =
            error.value ||
            'The code entered does not match the last code sent. Check the number and try again, or click resend code.';
    } else {
        if (data.value === 'passcode validated') {
            emit('passcodeValid', phoneType.value);
        }
    }
}

const alreadyHasPasscode = () => {
    passcodeSent.value = true;
};

const captureCode = (code: string) => {
    passcode.value = code;
};

const resendCode = () => {
    passcodeSent.value = false;
    validatePasscodeError.value = '';
};

const selectPhoneType = (value: PhoneType) => {
    phoneType.value = value;
};

const handleBackButton = () => {
    emit('backButtonPressed');
};

onMounted(() => {
    phoneType.value = clientPhoneIsTextable.value ? 'sms' : 'call';
});
</script>
